import type { MaintenanceRequestRow, MaintenanceStatus, TruckRow, WorkNote, WorkOrderPriority, WorkOrderRow, WorkOrderStatus } from '@routeback/core';

/** The plugin's Urgent / Medium / Low, plus High. `normal` is shown as Medium. */
export const PRIORITIES: { value: WorkOrderPriority; label: string }[] = [
  { value: 'urgent', label: 'Urgent' }, { value: 'high', label: 'High' }, { value: 'normal', label: 'Medium' }, { value: 'low', label: 'Low' },
];
export const PRIORITY_LABEL: Record<WorkOrderPriority, string> = { urgent: 'Urgent', high: 'High', normal: 'Medium', low: 'Low' };
const RANK: Record<WorkOrderPriority, number> = { urgent: 0, high: 1, normal: 2, low: 3 };

export const WO_STATUS_LABEL: Record<WorkOrderStatus, string> = {
  open: 'Open', in_progress: 'In progress', waiting_parts: 'Waiting on parts', done: 'Done', cancelled: 'Cancelled',
};
export const OPEN_STATUSES: readonly WorkOrderStatus[] = ['open', 'in_progress', 'waiting_parts'];
export const isOpen = (w: WorkOrderRow): boolean => !w.deleted_at && OPEN_STATUSES.includes(w.status);

/** What the driver's report shows for a job's status (server: private.work_orders_follow). */
export function requestStatusFor(s: WorkOrderStatus): MaintenanceStatus {
  return s === 'open' ? 'acknowledged' : s === 'done' ? 'done' : s === 'cancelled' ? 'declined' : 'in_progress';
}

/** "WO #12", or "New" until the server has numbered it. */
export const woLabel = (w: Pick<WorkOrderRow, 'number'>): string => (w.number ? `WO #${w.number}` : 'New');

/** Urgent first, then oldest first (the plugin's order). Scheduled jobs go last, by due date. */
export function sortOpen(rows: WorkOrderRow[]): WorkOrderRow[] {
  return rows.filter(isOpen).sort((a, b) => {
    const ra = a.recurring_task_id ? 1 : 0, rb = b.recurring_task_id ? 1 : 0;
    if (ra !== rb) return ra - rb;
    if (ra) return (a.due_on ?? a.opened_on).localeCompare(b.due_on ?? b.opened_on);
    return RANK[a.priority] - RANK[b.priority] || a.opened_on.localeCompare(b.opened_on) || (a.number ?? 1e9) - (b.number ?? 1e9);
  });
}

/** Driver reports nobody has dealt with yet, oldest first. */
export function newReports(reqs: MaintenanceRequestRow[]): MaintenanceRequestRow[] {
  return reqs.filter((r) => !r.deleted_at && r.status === 'submitted' && !r.work_order_id).sort((a, b) => a.created_at.localeCompare(b.created_at));
}

/** Scheduled (recurring) jobs count as "due soon" within this many days (the plugin's 30). */
export const DUE_SOON_DAYS = 30;
const plusDays = (d: string, n: number): string => {
  const t = new Date(`${d}T12:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};
const dueOf = (w: WorkOrderRow): string => w.due_on ?? w.opened_on;

/**
 * The Open list: open repair jobs. Scheduled services live on the Recurring
 * tab (Mike: they flooded the list) — the Open tab shows one line with how many
 * are overdue / due within 30 days. A scheduled job sent to the mechanic still
 * shows here, since someone is working on it.
 */
export function openList(rows: WorkOrderRow[], _today?: string): WorkOrderRow[] {
  return sortOpen(rows).filter((w) => !w.recurring_task_id || w.for_mechanic || w.status !== 'open');
}

export interface Summary { open: number; urgent: number; fromDrivers: number; withMechanic: number; serviceDue: number; overdue: number }
/** The plugin's strip: open (not counting scheduled service), urgent, service due soon, overdue — plus new driver reports and jobs with the mechanic. */
export function summarize(wos: WorkOrderRow[], reqs: MaintenanceRequestRow[], today: string): Summary {
  const open = wos.filter(isOpen);
  const jobs = open.filter((w) => !w.recurring_task_id);
  const sched = open.filter((w) => w.recurring_task_id);
  const horizon = plusDays(today, DUE_SOON_DAYS);
  return {
    open: jobs.length,
    urgent: jobs.filter((w) => w.priority === 'urgent').length,
    fromDrivers: newReports(reqs).length,
    withMechanic: open.filter((w) => w.for_mechanic).length,
    serviceDue: sched.filter((w) => dueOf(w) <= horizon).length,
    overdue: sched.filter((w) => dueOf(w) < today).length,
  };
}

export interface CompletedFilter { truckId: string; from: string; to: string; shop: string; mmrOnly: boolean }
export const NO_FILTER: CompletedFilter = { truckId: '', from: '', to: '', shop: '', mmrOnly: false };
/** The plugin's Completed tab filters: truck, date range, shop, MMR only. Newest first. */
export function filterCompleted(rows: WorkOrderRow[], f: CompletedFilter): WorkOrderRow[] {
  const shop = f.shop.trim().toLowerCase();
  return rows
    .filter((w) => !w.deleted_at && w.status === 'done')
    .filter((w) => !f.truckId || w.truck_id === f.truckId)
    .filter((w) => !f.from || (w.completed_on ?? '') >= f.from)
    .filter((w) => !f.to || (w.completed_on ?? '') <= f.to)
    .filter((w) => !shop || w.vendor.toLowerCase().includes(shop))
    .filter((w) => !f.mmrOnly || w.include_mmr)
    .sort((a, b) => (b.completed_on ?? '').localeCompare(a.completed_on ?? '') || (b.number ?? 0) - (a.number ?? 0));
}

/** "482.50", "$1,200", "" → cents (null when blank, NaN when not money). */
export function parseMoney(s: string): number | null {
  const t = s.replace(/[$,\s]/g, '');
  if (!t) return null;
  if (!/^\d+(\.\d{0,2})?$/.test(t)) return Number.NaN;
  return Math.round(parseFloat(t) * 100);
}
export const formatMoney = (cents: number | null): string =>
  cents == null ? '' : `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** "142,500" → 142500; "", "N/A" → null; anything else → NaN. */
export function parseOdometer(s: string): number | null {
  const t = s.replace(/[,\s]/g, '').replace(/mi(les)?$/i, '');
  if (!t || /^n\/?a$/i.test(t)) return null;
  if (!/^\d{1,7}$/.test(t)) return Number.NaN;
  return parseInt(t, 10);
}

export function addNote(notes: WorkNote[], text: string, by: string | null, name: string, at = new Date().toISOString()): WorkNote[] {
  const t = text.trim();
  return t ? [...notes, { at, by, name, text: t }] : notes;
}

/** Open jobs on the same truck — where a second report of the same problem can go. */
export function openJobsFor(wos: WorkOrderRow[], truckId: string | null): WorkOrderRow[] {
  return truckId ? sortOpen(wos).filter((w) => w.truck_id === truckId) : [];
}

const cell = (v: string | number | null | undefined): string => {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) || /^[=+\-@]/.test(s) ? `"${s.replace(/"/g, '""').replace(/^([=+\-@])/, "'$1")}"` : s;
};
/** The Completed tab's CSV export (spreadsheet-safe: formulas are neutralised). */
export function completedCsv(rows: WorkOrderRow[], trucks: Map<string, TruckRow>, names: Map<string, string>): string {
  const head = ['WO #', 'Unit', 'Priority', 'Opened', 'Reported by', 'Problem', 'Completed', 'Shop / performed by', 'Odometer', 'Cost', 'What was done', 'On MMR'];
  const lines = rows.map((w) => [
    w.number ?? '', trucks.get(w.truck_id ?? '')?.unit_number ?? '', PRIORITY_LABEL[w.priority], w.opened_on,
    w.reported_by ? names.get(w.reported_by) ?? '' : '', [w.title, w.description].filter(Boolean).join(' — '),
    w.completed_on ?? '', w.vendor, w.odometer ?? '', w.cost_cents == null ? '' : (w.cost_cents / 100).toFixed(2), w.work_done, w.include_mmr ? 'Yes' : 'No',
  ].map(cell).join(','));
  return [head.join(','), ...lines].join('\r\n') + '\r\n';
}
